import AuthPage from '@/components/features/auth/AuthPage'
import SendOTPForm from '@/components/features/auth/SendOTPForm'
import SignInForm from '@/components/features/auth/SignInForm'
import { useAuth } from '@/context/AuthContext'
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { useLayoutEffect, useState } from 'react'
import { z } from 'zod'

import { Button, Separator, safeRedirect } from '@droidmap/frontend-ui'

export const Route = createFileRoute('/_unauthenticated/login')({
  validateSearch: z.object({
    // Only same-origin relative paths as a post-login redirect (#2142).
    redirect: z
      .string()
      .optional()
      .transform((value) => safeRedirect(value)),
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: search.redirect ?? '/' })
    }
  },
  component: Login,
})

export default function Login() {
  const auth = useAuth()
  const router = useRouter()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [usePasswordAuth, setUsePasswordAuth] = useState(true)

  const handleSignIn = async (data: Parameters<typeof auth.signIn>[0]) => {
    const result = await auth.signIn(data)
    if (result.success) {
      router.invalidate()
    }
    return result
  }

  // Redirect to the page the user was on before logging in or to the home page if no redirect is provided
  useLayoutEffect(() => {
    if (auth.isAuthenticated) {
      router.history.push(search.redirect ?? '/')
    }
  }, [auth.isAuthenticated, search.redirect, router])

  return (
    <AuthPage
      title={
        usePasswordAuth ? 'Sign in to your account' : 'Sign in with a code'
      }
    >
      <div className="h-96">
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <div>
            {usePasswordAuth && <SignInForm onSignIn={handleSignIn} />}

            {!usePasswordAuth && (
              <SendOTPForm
                onRequestOTP={auth.requestOTP}
                onVerifyOTP={auth.verifyOTP}
              />
            )}

            <div className="mt-6">
              <Separator label="OR" />
            </div>

            <div className="mt-6">
              <Button
                variant="secondary"
                onClick={() => setUsePasswordAuth(!usePasswordAuth)}
                className="w-full"
              >
                {usePasswordAuth ? 'Sign in with a code instead' : 'Sign in'}
              </Button>
            </div>

            <p className="text-text mt-6 text-center text-sm">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate({ to: '/signup' })}
                className="font-semibold leading-6 text-accent hover:text-accent-hi"
              >
                Sign up here
              </button>
            </p>
          </div>
        </div>
      </div>
    </AuthPage>
  )
}
